/**
 * Date Utilities for Race Countdown and Training Plan Management
 */

export type RaceProximityStatus = 'distant' | 'approaching' | 'imminent' | 'race_week' | 'past';

export interface RaceDateInfo {
  formatted: string;
  daysUntil: number;
  weeksUntil: number;
  proximityStatus: RaceProximityStatus;
  displayText: string;
  colorClass: string;
}

/**
 * Calculate days until race date
 * @param raceDate - ISO date string or Date object
 * @returns Number of days until race (negative if past)
 */
export function calculateDaysUntilRace(raceDate: string | Date): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const race = new Date(raceDate);
  race.setHours(0, 0, 0, 0);

  const diffTime = race.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Determine race proximity status based on days until race
 */
export function getRaceProximityStatus(daysUntil: number): RaceProximityStatus {
  if (daysUntil < 0) return 'past';
  if (daysUntil <= 7) return 'race_week';
  if (daysUntil <= 21) return 'imminent';
  if (daysUntil <= 56) return 'approaching';
  return 'distant';
}

/**
 * Get color class for race proximity status
 */
export function getProximityColorClass(status: RaceProximityStatus): string {
  const colorMap: Record<RaceProximityStatus, string> = {
    distant: 'text-blue-600 bg-blue-50',
    approaching: 'text-yellow-600 bg-yellow-50',
    imminent: 'text-orange-600 bg-orange-50',
    race_week: 'text-red-600 bg-red-50',
    past: 'text-gray-500 bg-gray-50'
  };
  return colorMap[status];
}

/**
 * Get icon for race proximity status
 */
export function getProximityIcon(status: RaceProximityStatus): string {
  const iconMap: Record<RaceProximityStatus, string> = {
    distant: '📅',
    approaching: '⚡',
    imminent: '🔥',
    race_week: '🏁',
    past: '✅'
  };
  return iconMap[status];
}

/**
 * Format race date with countdown information
 */
export function formatRaceDateWithCountdown(raceDate: string | Date): RaceDateInfo {
  const daysUntil = calculateDaysUntilRace(raceDate);
  const weeksUntil = Math.ceil(daysUntil / 7);
  const proximityStatus = getRaceProximityStatus(daysUntil);

  const race = new Date(raceDate);
  const formatted = race.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

  let displayText: string;
  if (daysUntil < 0) {
    displayText = `Race completed ${Math.abs(daysUntil)} days ago`;
  } else if (daysUntil === 0) {
    displayText = 'Race day is today! 🏁';
  } else if (daysUntil === 1) {
    displayText = 'Race tomorrow!';
  } else if (daysUntil <= 7) {
    displayText = `${daysUntil} days until race`;
  } else {
    displayText = `${weeksUntil} ${weeksUntil === 1 ? 'week' : 'weeks'} (${daysUntil} days) until race`;
  }

  const colorClass = getProximityColorClass(proximityStatus);

  return {
    formatted,
    daysUntil,
    weeksUntil,
    proximityStatus,
    displayText,
    colorClass
  };
}

/**
 * Calculate progress percentage from plan start to race date
 */
export function calculateRaceProgress(planStartDate: string | Date, raceDate: string | Date): number {
  const start = new Date(planStartDate);
  start.setHours(0, 0, 0, 0);

  const race = new Date(raceDate);
  race.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const totalDays = (race.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  const elapsedDays = (today.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

  if (elapsedDays <= 0) return 0;
  if (elapsedDays >= totalDays) return 100;

  return Math.round((elapsedDays / totalDays) * 100);
}

/**
 * Format date range display (e.g., "Week 1: Jan 15 - Jan 21")
 */
export function formatWeekDateRange(weekStartDate: string | Date): string {
  const start = new Date(weekStartDate);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);

  const startFormatted = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });

  const endFormatted = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });

  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Get week number from plan start date
 */
export function getWeekNumberFromDate(planStartDate: string | Date, currentDate: string | Date): number {
  const start = new Date(planStartDate);
  start.setHours(0, 0, 0, 0);

  const current = new Date(currentDate);
  current.setHours(0, 0, 0, 0);

  const diffDays = (current.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);

  return Math.floor(diffDays / 7) + 1;
}

/**
 * Check if a date is in the current week
 */
export function isCurrentWeek(weekStartDate: string | Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const weekStart = new Date(weekStartDate);
  weekStart.setHours(0, 0, 0, 0);

  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  return today >= weekStart && today <= weekEnd;
}
